import { Button } from "@Light/components/page/Button";
import { ButtonFooter } from "@Light/components/page/ButtonFooter";
import { MutationButton } from "@Light/components/page/MutationButton";
import type { AddressSearchResponse } from "@Light/services/lightTypes";
import React from "react";
import { EnrollBottomSheet } from "../common/EnrollBottomSheet";
import { useEnrollment } from "../flow/context";
import { AddressCard } from "./AddressCard";

export type DifferentUtilityBottomSheetProps = {
  open: boolean;
  address: AddressSearchResponse | undefined;
  onConfirm: () => void;
  onClose: () => void;
};

/**
 * Warns that the location they picked is served by a different utility than the
 * offer they've already seen, so that offer has to be re-quoted.
 */
export const DifferentUtilityBottomSheet: React.FC<
  DifferentUtilityBottomSheetProps
> = ({ open, address, onConfirm, onClose }) => {
  const { saveMutation } = useEnrollment();

  return (
    <EnrollBottomSheet
      open={open}
      onClose={onClose}
      title={
        <>
          The offer differs for this address.
          <br />
          Do you want to view the new offer?
        </>
      }
      subtitle="The address you provided is serviced by a different local utility provider than your last offer. You’ll need to review a new offer to account for this change."
      data-testid="different-utility-sheet"
      footer={
        <ButtonFooter>
          <MutationButton
            size="lg"
            mutation={saveMutation}
            mutate={onConfirm}
            mutateButtonText="Yes, view offer"
            errorMessage="Failed to save your address. Please try again."
            data-testid="different-utility-sheet-yes"
          />
          <Button
            size="lg"
            buttonType="clear"
            className="w-full"
            onClick={onClose}
            data-testid="different-utility-sheet-no"
          >
            No, provide different address
          </Button>
        </ButtonFooter>
      }
    >
      {address ? <AddressCard address={address} /> : null}
    </EnrollBottomSheet>
  );
};
